import { joinURL } from 'ufo'
import type { StationeryPreviewImage } from '~~/layers/base/types/stationeryPreviewImage'
import { buildCloudflareExternalImgUrl } from '~~/layers/base/utils/buildCloudflareExternalImgUrl'

function isPlainRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function nonEmptyTrimmed(s: unknown): s is string {
    return typeof s === 'string' && s.trim().length > 0
}

/**
 * Runtime guard for one `preview_images` JSON entry (API / persistence validation).
 */
export function isStationeryPreviewImage(x: unknown): x is StationeryPreviewImage {
    if (typeof x !== 'object' || x === null) return false
    const o = x as Record<string, unknown>
    if (typeof o.label !== 'string' || !isPlainRecord(o.meta)) return false
    const hasCf = nonEmptyTrimmed(o.cloudflare_image_id)
    const hasExt = nonEmptyTrimmed(o.external_url)
    if (hasCf === hasExt) return false
    return true
}

/**
 * Envelope **Face** surface preview (often where a printed or handwritten address appears).
 *
 * Primary match for handwritten flow is `meta.partName: "Envelope"` + surface `name: "Face"`.
 * Fallbacks handle legacy/variant payloads (`label` and `meta.surfaceId` / `surface_id` === 3).
 *
 * **HOMS:** Use this (or `pickDenormalizedEnvelopeFacePreview` in `handwritingProjectDenormalizedPreview.ts`)
 * to select the `preview_images[]` row before calling `resolveEnvelopeFaceSafeAreaInchesFromPreviewMeta` on
 * that row's `meta`.
 */
export function isStationeryPreviewEnvelopeFace(preview: StationeryPreviewImage): boolean {
    const meta = (preview.meta ?? {}) as {
        partName?: unknown
        part_name?: unknown
        surfaceId?: unknown
        surface_id?: unknown
    }
    const partName = String(meta.partName ?? meta.part_name ?? '')
        .toLowerCase()
        .trim()
    const surfaceName = String(preview.name ?? '')
        .toLowerCase()
        .trim()
    if (partName === 'envelope' && surfaceName === 'face') return true

    const label = String(preview.label ?? '')
        .toLowerCase()
        .trim()
    if (label === 'face' || label === 'envelope-face') return true
    const rawSurfaceId = meta.surfaceId ?? meta.surface_id
    const surfaceId =
        typeof rawSurfaceId === 'number'
            ? rawSurfaceId
            : typeof rawSurfaceId === 'string'
              ? Number(rawSurfaceId)
              : NaN
    return Number.isFinite(surfaceId) && surfaceId === 3
}

/**
 * Best image for the **back** of the stacked envelope preview: **Face** (address side) when present,
 * otherwise **Liner** (inside / paper-backed art).
 */
export function pickEnvelopeBackLayerPreview(
    previews: StationeryPreviewImage[] | null | undefined
): StationeryPreviewImage | null {
    if (!previews?.length) return null
    const face = previews.find(isStationeryPreviewEnvelopeFace)
    if (face) return face
    return pickLinerPreview(previews)
}

/** Stationery catalog **Liner** row (`label === 'Liner'`), used as the StackedPreviewImages back layer. */
export function pickLinerPreview(
    previews: StationeryPreviewImage[] | null | undefined
): StationeryPreviewImage | null {
    if (!previews?.length) return null
    return previews.find((p) => p.label?.trim() === 'Liner') ?? null
}

/**
 * Numeric `surfaceId` / `surface_id` from preview meta (Front=1, Back=2, Face=3, Flap=4…).
 */
function previewMetaSurfaceId(meta: Record<string, unknown> | undefined): number | undefined {
    if (!meta) return undefined
    const raw = meta.surfaceId ?? meta.surface_id
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw
    if (typeof raw === 'string' && raw.trim() !== '') {
        const n = Number(raw)
        if (Number.isFinite(n)) return n
    }
    return undefined
}

/** `surfaceIds` value for the envelope flap surface (Front=1, Back=2, Face=3, Flap=4). */
export const ENVELOPE_FLAP_SURFACE_ID = 4

/**
 * Whether a preview row `label` refers to the envelope **flap** bitmap.
 *
 * Normalizes `label` and matches `flap` or the legacy `envelope-flap` spelling; explicit flap rows are
 * preferred over the {@link ENVELOPE_FLAP_SURFACE_ID} fallback.
 */
export function isEnvelopeFlapPreviewLabel(label: string | undefined | null): boolean {
    const normalized = label?.toLowerCase().trim() ?? ''
    return normalized === 'flap' || normalized === 'envelope-flap'
}

/**
 * Envelope **Flap** surface preview (return-address artwork side).
 *
 * Mirrors {@link isStationeryPreviewEnvelopeFace}: `meta.partName: "Envelope"` + surface `name: "Flap"`
 * first, then the shared label spellings, then `meta.surfaceId` / `surface_id`.
 */
export function isStationeryPreviewEnvelopeFlap(preview: StationeryPreviewImage): boolean {
    const meta = preview.meta ?? {}
    const partName = String(meta.partName ?? meta.part_name ?? '')
        .toLowerCase()
        .trim()
    const surfaceName = String(preview.name ?? '')
        .toLowerCase()
        .trim()
    if (partName === 'envelope' && surfaceName === 'flap') return true
    if (isEnvelopeFlapPreviewLabel(preview.label)) return true
    return previewMetaSurfaceId(meta) === ENVELOPE_FLAP_SURFACE_ID
}

/** First envelope-flap row in `preview_images`, or null when the stationery has no flap art. */
export function pickEnvelopeFlapPreview(
    previews: StationeryPreviewImage[] | null | undefined
): StationeryPreviewImage | null {
    if (!previews?.length) return null
    return previews.find(isStationeryPreviewEnvelopeFlap) ?? null
}

/**
 * Card-front preview used for paper swatch sampling / stack front (surface 1, not envelope/liner).
 * Also accepts explicit Front labels when `surfaceId` is missing.
 */
export function isStationeryPreviewCardFront(preview: {
    label?: string
    meta?: Record<string, unknown>
}): boolean {
    const meta = preview.meta ?? {}
    const partName = String(meta.partName ?? meta.part_name ?? '')
        .toLowerCase()
        .trim()
    const label = String(preview.label ?? '')
        .toLowerCase()
        .trim()
    if (partName.includes('envelope') || partName.includes('liner')) return false
    if (label.includes('envelope') || label.includes('liner')) return false
    const sid = previewMetaSurfaceId(meta)
    if (sid === 1) return true
    return label === 'front' || label === 'card-front'
}

/**
 * First card-front row in `preview_images`, or null when none match.
 */
export function pickCardFrontPreview(
    previews: StationeryPreviewImage[] | null | undefined
): StationeryPreviewImage | null {
    if (!previews?.length) return null
    return previews.find(isStationeryPreviewCardFront) ?? null
}

/** Named Cloudflare Images variant segment (`…/id/partPreview`), aligned with catalog preview URLs. */
const CLOUDFLARE_IMAGES_PREVIEW_VARIANT = 'partPreview' as const

/**
 * Public delivery URL for a persisted preview row: Cloudflare Images id (`…/id/partPreview`), or
 * external URL via `cdn-cgi/image` passthrough ({@link buildCloudflareExternalImgUrl}). Arbitrary
 * external URLs are not Cloudflare Images rows, so they do not use the `partPreview` path segment.
 */
export function resolveStationeryPreviewDeliveryUrl(
    preview: Pick<StationeryPreviewImage, 'cloudflare_image_id' | 'external_url'>,
    cloudflareImagesBaseUrl: string
): string | null {
    const ext = typeof preview.external_url === 'string' ? preview.external_url.trim() : ''
    if (ext) {
        return buildCloudflareExternalImgUrl(ext, {})
    }
    const baseUrl = String(cloudflareImagesBaseUrl ?? '').replace(/\/$/, '')
    const id =
        typeof preview.cloudflare_image_id === 'string' ? preview.cloudflare_image_id.trim() : ''
    if (!baseUrl || !id) return null
    return joinURL(baseUrl, id, CLOUDFLARE_IMAGES_PREVIEW_VARIANT)
}
